import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Hook for setting a producer's primary place via producer_places (canonical relation).
 * Also keeps legacy producers.place_id in sync for backwards compatibility.
 */
export function useSetProducerPlace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ producerId, placeId }: { producerId: string; placeId: string | null }) => {
      if (placeId) {
        // 1. Clear any existing primary
        await supabase
          .from("producer_places" as any)
          .update({ is_primary: false })
          .eq("producer_id", producerId)
          .eq("is_primary", true)
          .neq("place_id", placeId);

        // 2. Upsert the new primary relation
        const { error: upsertError } = await supabase
          .from("producer_places" as any)
          .upsert(
            { producer_id: producerId, place_id: placeId, is_primary: true },
            { onConflict: "producer_id,place_id" }
          );

        if (upsertError) {
          console.error("[useSetProducerPlace] upsert failed:", upsertError);
          throw upsertError;
        }
      } else {
        // Remove all place relations
        const { error: deleteError } = await supabase
          .from("producer_places" as any)
          .delete()
          .eq("producer_id", producerId);

        if (deleteError) {
          console.error("[useSetProducerPlace] delete failed:", deleteError);
          throw deleteError;
        }
      }

      // 3. Keep legacy producers.place_id in sync
      const { error: legacyError } = await supabase
        .from("producers")
        .update({ place_id: placeId, updated_at: new Date().toISOString() })
        .eq("id", producerId);

      if (legacyError) {
        console.error("[useSetProducerPlace] legacy sync failed:", legacyError);
        // Non-fatal: relation table is canonical
      }

      console.log("[useSetProducerPlace] saved", { producerId, placeId });
      return { id: producerId, place_id: placeId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["producer", data.id] });
      queryClient.invalidateQueries({ queryKey: ["my-producers"] });
      queryClient.invalidateQueries({ queryKey: ["producer-capabilities", data.id] });
      queryClient.invalidateQueries({ queryKey: ["published-places"] });
      queryClient.invalidateQueries({ queryKey: ["place-producers"] });

      if (data.place_id) {
        toast({
          title: "Plats kopplad",
          description: "Din gård är nu kopplad till platsen.",
        });
      } else {
        toast({
          title: "Platskoppling borttagen",
          description: "Din gård är inte längre kopplad till någon plats.",
        });
      }
    },
    onError: (error: any) => {
      console.error("[useSetProducerPlace] error:", error);
      const code = error?.code || "UNKNOWN";
      const message = error?.message || "Okänt fel";
      toast({
        title: "Kunde inte uppdatera plats",
        description: `${code}: ${message}`,
        variant: "destructive",
      });
    },
  });
}
